import { AlertCircle } from "lucide-react";
import { formList, useForm, yupResolver } from "@mantine/form";
import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Select,
  Space,
  Stepper,
  TextInput,
} from "@mantine/core";
import { Navigate } from "react-router-dom";
import EmployeeImportFileUpload from "../elements/EmployeeImportFileUpload";
import EmployeeImportConfigureSelect from "../elements/EmployeeImportConfigureSelect";
import EmployeeReferenceColumnSelect from "../elements/EmployeeReferenceColumnSelect";
import useEmployeeUploadApiService from "../services/api/EmployeeUploadApiSerivce";
import { AuthContext } from "../../../providers/AuthProvider";
import { queryClient } from "../../../App";
import { HttpClientError } from "../../../providers/HttpClient";
import EmployeeUploadFormValidationSchema from "../services/validator/EmployeeUploadFormValidationSchema";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type EmployeeUploadFormProps = {
  onSuccess?: () => void;
  onFailure?: () => void;
};

const EmployeeUploadForm: React.FC<EmployeeUploadFormProps> = ({
  onFailure,
  onSuccess,
}) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();
  const [active, setActive] = useState(0);

  const { isLoading, isError, error, isSuccess, mutate } =
    useEmployeeUploadApiService({
      employer_id: auth?.employer.id ?? 0,
    });

  useEffect(() => {
    if (isError) {
      showToast({
        strapiId: "employee_upload_form_failed",
        data: { message: HttpClientError(error) },
      });

      if (onFailure) {
        onFailure();
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess) {
      queryClient
        .invalidateQueries([
          "EmployeeDataImportListApiService",
          auth?.employer.id,
        ])
        .then(() => {
          if (onSuccess) {
            onSuccess();
          }
          showToast({ strapiId: "employee_upload_form_success" });
        });
    }
  }, [isSuccess]);

  const nextStep = (): void =>
    setActive((current) => (current < 3 ? current + 1 : current));
  const prevStep = (): void =>
    setActive((current) => (current > 0 ? current - 1 : current));

  const form = useForm({
    schema: yupResolver(EmployeeUploadFormValidationSchema),
    initialValues: {
      file: undefined,
      name: "",
      configurationToUse: "",
      columnHeaders: formList([
        {
          name: "",
          relatedColumn: "",
          isTag: false,
        },
      ]),
      importAction: "",
      lookupHeader: "",
      useExistingConfiguration: false,
    },
  });

  const onSubmitHandler = (formValues: typeof form.values): void => {
    if (formValues.file) {
      mutate({
        file: formValues.file,
        lookup_header: formValues.lookupHeader,
        configuration_is_customized: false.toString(),
        overwrite_configuration: false.toString(),
        delete_missing: Object.is(
          formValues.importAction,
          "create_update_delete"
        ).toString(),
        use_existing_configuration:
          formValues.useExistingConfiguration.toString(),
        employer_data_import_configuration_name: formValues.name,
        employer: auth?.employer.id.toString() ?? "0",
        custom_configuration: JSON.stringify(
          formValues.columnHeaders.map((it) => ({
            name: it.name,
            swRelatedColumn: it.relatedColumn,
            is_tag: it.isTag,
          }))
        ),
      });
    }
  };

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <form
      onSubmit={form.onSubmit((formValues) =>
        active === 3 ? onSubmitHandler(formValues) : nextStep()
      )}
    >
      <Stepper active={active} onStepClick={setActive} breakpoint="sm">
        <Stepper.Step label="First step" description="Upload Employee">
          <Space h="xl" />
          <EmployeeImportFileUpload form={form} onDone={nextStep} />
          <Space h="xl" />
        </Stepper.Step>
        <Stepper.Step label="Second step" description="Map Columns">
          <Space h="xl" />
          <EmployeeImportConfigureSelect form={form} auth={auth} />
          <Space h="xl" />
          <EmployeeReferenceColumnSelect form={form} />
        </Stepper.Step>
        <Stepper.Step label="Final step" description="Confirm & Process">
          <TextInput
            placeholder="Configuration Name"
            required
            sx={{ flex: 1 }}
            {...form.getInputProps("name")}
          />
          <Space h="xl" />
          <Select
            required
            placeholder="Select an matching data"
            data={[
              { value: "employee_no", label: "Employee Number" },
              { value: "id_number", label: "ID Number" },
              { value: "smartwage_id", label: "Jem ID" },
              { value: "id", label: "Database Number" },
              {
                value: "id_number_and_employee_number",
                label: "ID Number/Employee No",
              },
            ]}
            {...form.getInputProps("lookupHeader")}
          />
          <Space h="xl" />
          <Select
            id="importAction"
            name="importAction"
            searchable
            clearable
            data={[
              {
                value: "create_update",
                label: "Create/Update",
              },
              {
                value: "create_update_delete",
                label: "Create/Update/Delete",
              },
            ]}
            {...form.getInputProps("importAction")}
            placeholder="Select an action"
            required
          />
        </Stepper.Step>
        <Stepper.Completed>
          <Space h="xl" />
          <Alert
            icon={<AlertCircle size={16} />}
            title="Warning!"
            color="yellow"
          >
            Are you sure you want import the data in this file?
          </Alert>
          <Space h="xl" />
          <div className="overflow-hidden bg-white shadow sm:rounded-lg">
            <div className="border-t border-gray-200">
              <dl>
                <div className="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Filename
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {form.values.file && form.values.file["name" as string]}
                  </dd>
                </div>

                <div className="px-4 py-5 bg-white sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Configuration name
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {form.values.name}
                  </dd>
                </div>
                <div className="px-4 py-5 bg-gray-50 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Import action
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {form.values.importAction}
                  </dd>
                </div>
                <div className="px-4 py-5 bg-white sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">
                    Lookup Header
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {form.values.lookupHeader}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </Stepper.Completed>
      </Stepper>

      {active !== 0 && (
        <Group position="center" mt="xl">
          <Button variant="default" onClick={prevStep} color="jem-theme">
            Back
          </Button>
          <Button type="submit" loading={isLoading} color="jem-theme">
            {active === 3 ? "Import" : "Continue"}
          </Button>
        </Group>
      )}
    </form>
  );
};

export default EmployeeUploadForm;
